import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { SectionCard, EmptyState, Money } from "@/components/common/ui";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps } from "@/lib/chart";
import { axisINR, formatCount } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from "recharts";
import { Gift, Home, Coins, Receipt } from "lucide-react";

const ACCENT = "hsl(var(--chart-cat-6))";

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-xl border bg-card p-3.5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: `${ACCENT}1f`, color: ACCENT }}>
          <Icon className="h-4 w-4" />
        </span>
        <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      </div>
      <p className="mt-2 font-heading text-xl font-semibold tabular-nums tracking-tight">{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

/**
 * Dashboard Referral Payouts card — referral invoices (platform = "Referral")
 * scoped to the current dashboard project + month selection, with their CX
 * booking impact. Lives in the "Where the Money Goes" group.
 */
export function ReferralPayouts({ period = "FY", projects = null }) {
  const { money } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true; setLoading(true);
    api.referralTracker(period, projects && projects.length ? projects : null)
      .then((d) => { if (on) { setData(d); setLoading(false); } })
      .catch(() => on && setLoading(false));
    return () => { on = false; };
  }, [period, JSON.stringify(projects)]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = data?.totals;
  const rows = (data?.rows || []).slice(0, 8).map((r) => ({ name: r.project, spend: r.spend })).filter((d) => d.spend > 0).reverse();

  return (
    <SectionCard
      title="Referral Payouts"
      description="Referral payout invoices (platform = Referral) and the CX bookings they helped drive — scoped to the selected projects and month."
      testId="card-mh-referral">
      {loading && !data ? (
        <div className="p-4"><Skeleton className="h-24 w-full" /></div>
      ) : !t || t.spend <= 0 ? (
        <div className="py-8"><EmptyState title="No referral payouts" message="No referral invoices for this selection." /></div>
      ) : (
        <div className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat icon={Gift} label="Referral Payouts" value={<Money value={t.spend} />} sub={`${formatCount(t.count)} invoice(s)`} testId="referral-kpi-spend" />
            <Stat icon={Receipt} label="Outstanding" value={<Money value={t.outstanding} />} sub="Yet to be paid" testId="referral-kpi-outstanding" />
            <Stat icon={Home} label="CX Bookings" value={formatCount(t.cx_bookings)} sub="Booking impact" testId="referral-kpi-bookings" />
            <Stat icon={Coins} label="Cost / Booking" value={t.cost_per_booking != null ? <Money value={t.cost_per_booking} /> : "—"} sub="Payout ÷ CX bookings" testId="referral-kpi-cpb" />
          </div>

          {rows.length > 0 && (
            <div style={{ height: Math.max(160, rows.length * 30) }} data-testid="referral-project-bars">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} layout="vertical" margin={{ top: 2, right: 64, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                  <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                  <YAxis type="category" dataKey="name" width={118} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} interval={0} />
                  <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                  <Bar dataKey="spend" name="Referral payout" radius={[0, 5, 5, 0]} barSize={15} isAnimationActive={false} fill={ACCENT}>
                    {rows.map((_, i) => <Cell key={i} />)}
                    <LabelList dataKey="spend" position="right" formatter={(v) => money(v)} style={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} className="tabular-nums" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
